import { useMemo, useRef } from 'react';
import { TableHeightRefWrapper } from '@dealstack/frontend/app/pages/Valuations/components/table/styled';
import { BaseValuationRow } from '@dealstack/frontend/app/pages/Valuations/components/table/types';
import {
	PersonSummary,
	SecurityBreakdown,
	ValuationSummaryGroupByPersonRes,
} from '@dealstack/shared/lib/generated';
import {
	CustomRow,
	FXRate,
} from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/types';
import {
	ColumnDef,
	getCoreRowModel,
	getExpandedRowModel,
	useReactTable,
} from '@tanstack/react-table';
import { ScrollArea } from '@mantine/core';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import {
	formatSummaryDecimal,
	formatSummaryDecimalString,
	formatSummaryNumber,
} from '@dealstack/frontend/app/pages/Valuations/components/table/utils/formatting';
import {
	SourcesUsesTotalsRowItemStyled,
	SourcesUsesTotalsRowStyled,
} from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/components/styled';
import { AlignRight } from '@dealstack/frontend/app/pages/Valuations/components/styled';
import {
	isReadOnlyRow,
	useSourcesUsesSecuritiesColumns,
} from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/components/SourcesUsesSecuritiesTable.utils';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import { flexRender } from '@dealstack/frontend/shared/tables/flexRender';
import { VALUATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';

interface Props {
	person: PersonSummary;
	summary: ValuationSummaryGroupByPersonRes;
	customRows?: CustomRow[];
	baseCurrency?: string;
	fxRates?: FXRate[];
	isLoading?: boolean;
	onScrollStateChange?: (isScrolling: boolean) => void;
	onScrollbarWidthChange?: (scrollbarWidth: number) => void;
	onDeleteRow?: (rowId: string) => void;
	onFieldChange?: (index: number, fieldPath: string, value: unknown) => void;
	onFieldBlur?: (index: number) => void;
	variant: 'inputs' | 'distributions' | 'proceeds';
}

export const SourcesUsesSecuritiesTable = ({
	person,
	summary,
	customRows,
	baseCurrency = 'EUR',
	fxRates,
	isLoading = false,
	onScrollStateChange,
	onScrollbarWidthChange,
	onDeleteRow,
	onFieldChange,
	onFieldBlur,
	variant,
}: Props) => {
	const tableHeightRef = useRef<HTMLDivElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const tValuations = useTranslationValuations();

	const columnProportions = useMemo(() => ['40%', '25%', '25%', '10%'] as const, []);

	const transformedData = useMemo(() => {
		if (!person) {
			return [] as BaseValuationRow[];
		}

		if (variant === 'inputs') {
			const rows = (customRows ?? []).map((cr): BaseValuationRow => {
				let convertedAmount = '';
				const amountNum = new HighPrecisionDecimal(cr.amount ?? 0);
				if (baseCurrency && cr.currency && cr.currency !== baseCurrency) {
					const rate = fxRates?.find((r) => r.currency === cr.currency)?.rate;
					if (rate) {
						// FX-converted amounts are monetary → snap to an exact 2dp fraction (the
						// formatter is format-only and would otherwise render the full repeating
						// quotient).
						convertedAmount = formatSummaryDecimal(amountNum.div(rate).toMoney());
					}
				} else if (baseCurrency && (!cr.currency || cr.currency === baseCurrency)) {
					convertedAmount = formatSummaryDecimal(amountNum);
				}

				const id = `${person.id}-${cr.id}`;
				const showActions = !!onDeleteRow && !isReadOnlyRow(cr.id);

				// Handle hierarchical rows (warrant receivables/payables)
				if (cr.subRows && cr.subRows.length > 0) {
					const subRowsTransformed = cr.subRows.map((subCr): BaseValuationRow => {
						const subAmountNum = new HighPrecisionDecimal(subCr.amount ?? 0);
						let subConvertedAmount = '';
						if (baseCurrency && subCr.currency && subCr.currency !== baseCurrency) {
							const rate = fxRates?.find((r) => r.currency === subCr.currency)?.rate;
							if (rate) {
								// FX-converted amount is monetary → snap to an exact 2dp fraction.
								subConvertedAmount = formatSummaryDecimal(
									subAmountNum.div(rate).toMoney()
								);
							}
						} else if (
							baseCurrency &&
							(!subCr.currency || subCr.currency === baseCurrency)
						) {
							subConvertedAmount = formatSummaryDecimal(subAmountNum);
						}
						return {
							id: `${person.id}-${subCr.id}`,
							values: {
								name: subCr.name ?? 'Unknown',
								inputAmount: formatSummaryDecimal(subAmountNum, true),
								convertedAmount: subConvertedAmount,
							},
							metadata: {
								type: 'custom',
								depth: 1,
								disabled: subCr.disabled ?? false,
								excludedFromTotal: subCr.excludedFromTotal,
							},
						};
					});

					return {
						id,
						values: {
							name: cr.name ?? 'Unnamed Input',
							inputAmount: formatSummaryDecimal(amountNum, true),
							convertedAmount,
						},
						metadata: {
							type: 'securityName',
							depth: 0,
							hasToggle: true,
							isToggled: false,
							securityPersonCount: cr.expandableCount ?? cr.subRows.length,
							disabled: cr.disabled ?? false,
							excludedFromTotal: cr.excludedFromTotal,
						},
						subRows: subRowsTransformed,
					};
				}

				return {
					id,
					values: {
						name: cr.name ?? 'Unnamed Input',
						inputAmount: formatSummaryDecimal(amountNum, true),
						convertedAmount,
						...(showActions ? { actions: id } : {}),
					},
					metadata: {
						type: 'custom',
						depth: 1,
						disabled: cr.disabled ?? false,
						pending: cr.pending ?? false,
						index: cr.index,
					},
				};
			});
			return rows;
		}

		if (!summary) {
			return [] as BaseValuationRow[];
		}

		if (variant === 'distributions') {
			const securities = person.groups
				.flatMap((g) => g.outflows.securitiesBreakdown ?? [])
				.filter(Boolean);
			if (securities.length === 0) {
				return [];
			}
			const names = [...new Set(securities.map((s) => s.name))].sort((a, b) =>
				a.toLowerCase().localeCompare(b.toLowerCase())
			);
			const rows: BaseValuationRow[] = [];
			const personIdToName = new Map<string, string>(
				summary.persons.map((p) => [p.id, p.displayName])
			);
			const mipIdToName = new Map<string, string>(
				summary.persons.flatMap((p) =>
					p.groups.filter((g) => !!g.id).map((g) => [g.id ?? '', g.name ?? ''])
				)
			);

			for (const secName of names) {
				const items = securities.filter((s) => s.name === secName);
				const totalAmount = items.reduce(
					(sum, s) => sum.add(s.amount ?? 0),
					new HighPrecisionDecimal(0)
				);
				const holderIds = [...new Set(items.map((s) => s.holderId))];
				const validHolderIds = holderIds.filter((hid) => personIdToName.has(hid));
				const securityPersonCount = validHolderIds.length;
				const topRow: BaseValuationRow = {
					id: `${person.id}-${secName}`,
					values: {
						name: `${secName}`,
						inputAmount: formatSummaryDecimal(totalAmount),
						convertedAmount: baseCurrency ? formatSummaryDecimal(totalAmount) : '',
						actions: '',
					},
					metadata: {
						type: 'securityName',
						depth: 0,
						hasToggle: items.length > 0,
						isToggled: false,
						securityPersonCount,
					},
					subRows: [],
				};
				const subRows = items.map((s: SecurityBreakdown): BaseValuationRow => {
					const holderName = personIdToName.get(s.holderId) ?? 'Unknown Holder';
					const holderGroupName =
						mipIdToName.get(s.holderGroupId ?? '') ?? 'Unknown Group';
					const name = s.holderGroupId
						? `${holderName} (${holderGroupName})`
						: holderName;

					return {
						id: `${person.id}-${secName}-${s.id}`,
						values: {
							name,
							inputAmount: formatSummaryDecimalString(s.amount ?? '0'),
							convertedAmount: baseCurrency
								? formatSummaryDecimalString(s.amount ?? '0')
								: '',
							actions: '',
						},
						metadata: { type: 'security', depth: 1 },
					};
				});
				if (subRows.length > 0) {
					topRow.subRows = subRows;
				}
				rows.push(topRow);
			}
			return rows;
		}

		// proceeds
		const securities = person.groups
			.flatMap((g) => g.proceeds.securitiesBreakdown ?? [])
			.filter(Boolean);
		if (securities.length === 0) {
			return [];
		}
		const byIssuer = new Map<string, typeof securities>();
		for (const s of securities) {
			const issuerId = s.issuerId;
			if (!byIssuer.has(issuerId)) {
				byIssuer.set(issuerId, []);
			}
			byIssuer.get(issuerId)?.push(s);
		}
		const rows: BaseValuationRow[] = [];
		for (const [issuerId, issuerSecurities] of byIssuer.entries()) {
			const issuerPerson = summary.persons.find((p) => p.id === issuerId);
			const issuerName = issuerPerson ? issuerPerson.displayName : 'Unknown Issuer';
			const sorted = [...issuerSecurities].sort((a, b) =>
				a.name.toLowerCase().localeCompare(b.name.toLowerCase())
			);
			const securityRows = sorted.map(
				(s): BaseValuationRow => ({
					id: `${person.id}-${s.id}`,
					values: {
						name: s.name,
						inputAmount: formatSummaryDecimalString(s.amount ?? '0'),
						convertedAmount: formatSummaryDecimalString(s.amount ?? '0'),
						actions: '',
					},
					metadata: { type: 'security', depth: 1 },
				})
			);
			const totalAmount = sorted.reduce(
				(sum, s) => sum.add(s.amount ?? 0),
				new HighPrecisionDecimal(0)
			);
			rows.push({
				id: `${person.id}-${issuerId}`,
				values: {
					name: issuerName,
					inputAmount: formatSummaryDecimal(totalAmount),
					convertedAmount: baseCurrency ? formatSummaryDecimal(totalAmount) : '',
					actions: '',
				},
				subRows: securityRows,
				metadata: { type: 'issuer', depth: 0, securityPersonCount: securityRows.length },
			});
		}
		return rows;
	}, [variant, person, summary, customRows, baseCurrency, fxRates, onDeleteRow]);

	const columnDefs: ColumnDef<BaseValuationRow, unknown>[] = useSourcesUsesSecuritiesColumns({
		variant,
		onDeleteRow,
		onFieldChange,
		onFieldBlur,
	});

	const table = useReactTable({
		data: transformedData,
		columns: columnDefs,
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getSubRows: (row) => row.subRows ?? [],
		state: {},
	});

	const totalRow = useMemo(() => {
		if (variant === 'inputs') {
			const rows = customRows ?? [];
			if (rows.length <= 0) {
				return null;
			}
			// Exclude rows marked as excludedFromTotal (e.g. exercise prices) from subtotals
			const nonExchangeRows = rows.filter((r) => !r.excludedFromTotal);
			const totalAmount = nonExchangeRows.reduce((sum, r) => sum + Number(r.amount ?? 0), 0);
			let totalConverted = 0;
			if (baseCurrency && (fxRates?.length ?? 0) > 0) {
				totalConverted = nonExchangeRows.reduce((sum, r) => {
					if (!r.currency || r.currency === baseCurrency) {
						return sum + Number(r.amount ?? 0);
					}
					const rate = fxRates?.find((x) => x.currency === r.currency)?.rate;
					return rate ? sum + Number(r.amount ?? 0) / rate : sum;
				}, 0);
			} else {
				totalConverted = totalAmount;
			}
			return {
				id: 'total',
				values: {
					name: 'Total',
					inputAmount: formatSummaryNumber(totalAmount),
					convertedAmount: formatSummaryNumber(totalConverted),
					actions: '',
				},
				metadata: { type: 'total', depth: 0 },
			} as BaseValuationRow;
		}
		if (!person) {
			return null;
		}
		if (variant === 'distributions') {
			const securities = person.groups.flatMap((g) => g.outflows.securitiesBreakdown ?? []);
			if ((securities?.length ?? 0) <= 0) {
				return null;
			}
			const totalAmount = securities.reduce(
				(sum, s) => sum.add(s.amount ?? 0),
				new HighPrecisionDecimal(0)
			);
			return {
				id: 'distributions-total',
				values: {
					name: 'Total',
					inputAmount: formatSummaryDecimal(totalAmount),
					convertedAmount: baseCurrency ? formatSummaryDecimal(totalAmount) : '',
					actions: '',
				},
				metadata: { type: 'total', depth: 0, isTotal: true },
			} as BaseValuationRow;
		}
		// proceeds
		const securities = person.groups.flatMap((g) => g.proceeds.securitiesBreakdown ?? []);
		if ((securities?.length ?? 0) <= 0) {
			return null;
		}
		const totalAmount = securities.reduce(
			(sum, s) => sum.add(s.amount ?? 0),
			new HighPrecisionDecimal(0)
		);
		return {
			id: 'proceeds-total',
			values: {
				name: 'Total',
				inputAmount: formatSummaryDecimal(totalAmount),
				convertedAmount: formatSummaryDecimal(totalAmount),
				actions: '',
			},
			metadata: { type: 'total', depth: 0, isTotal: true },
		} as BaseValuationRow;
	}, [variant, customRows, baseCurrency, fxRates, person]);

	return (
		<div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<ScrollArea
				style={{ maxHeight: '800px', minHeight: '3rem', flex: 1 }}
				type="auto"
				scrollbarSize={8}
				scrollHideDelay={1000}
				onScrollPositionChange={(position) => {
					const isScrolling = position.y > 0 || position.x > 0;
					onScrollStateChange?.(isScrolling);
					// Use provided scrollbarSize as an approximation for width
					onScrollbarWidthChange?.(8);
				}}
			>
				<TableHeightRefWrapper
					data-testid={VALUATION_ELEMENT_ID.SOURCES_AND_USES_TABLE}
					ref={tableHeightRef}
					disableHeightGrowth
					style={{ overflow: 'visible', maxHeight: 'none' }}
				>
					<div ref={scrollContainerRef} style={{ overflow: 'visible' }}>
						<table
							data-testid={VALUATION_ELEMENT_ID.SOURCES_AND_USES_TABLE_SPACE}
							style={{
								width: '100%',
								borderCollapse: 'separate',
								borderSpacing: 0,
								tableLayout: columnProportions ? 'fixed' : 'auto',
							}}
						>
							<tbody>
								{isLoading && transformedData.length === 0 ? (
									<tr>
										<td style={{ padding: '0.75rem' }}>Loading…</td>
									</tr>
								) : (
									table.getRowModel().rows.map((row, rowIndex) => (
										<tr
											key={row.id}
											data-testindex={rowIndex}
											style={{ maxHeight: '3rem' }}
										>
											{row.getVisibleCells().map((cell, cellIndex) => {
												const proportionalWidth =
													columnProportions?.[cellIndex];
												const alignment = (
													cell.column.columnDef.meta as {
														alignment?: 'start' | 'end';
													}
												)?.alignment;

												const isLastCell =
													cellIndex === row.getVisibleCells().length - 1;

												return (
													<td
														key={cell.id}
														data-testindex={cellIndex}
														style={{
															paddingLeft: '0.5rem',
															paddingRight: '0.5rem',
															minHeight: '3rem',
															height: '3rem',
															maxHeight: '3rem',
															overflow: 'hidden',
															verticalAlign: 'middle',
															lineHeight: '1.5',
															borderRight: isLastCell
																? 'none'
																: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
															borderBottom: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
															...(proportionalWidth
																? { width: proportionalWidth }
																: {}),
															...(alignment === 'end'
																? { textAlign: 'right' }
																: {}),
														}}
													>
														{flexRender(
															cell.column.columnDef.cell,
															cell.getContext()
														)}
													</td>
												);
											})}
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</TableHeightRefWrapper>
			</ScrollArea>
			{totalRow && table.getRowModel().rows.length > 0 && (
				<SourcesUsesTotalsRowStyled
					data-testid={VALUATION_ELEMENT_ID.SOURCES_AND_USES_TABLE_TOTAL_ROW}
				>
					<SourcesUsesTotalsRowItemStyled>
						<TextV2 variant="sans/14/600">
							{tValuations('sources_and_uses.total')}
						</TextV2>
					</SourcesUsesTotalsRowItemStyled>
					<SourcesUsesTotalsRowItemStyled>
						<AlignRight>
							<TextV2
								data-testid={
									VALUATION_ELEMENT_ID.SOURCES_AND_USES_TABLE_TOTAL_VALUE_INPUT_AMOUNT
								}
								variant="sans/14/600"
							>
								{totalRow.values?.inputAmount ?? ''}
							</TextV2>
						</AlignRight>
					</SourcesUsesTotalsRowItemStyled>
					<SourcesUsesTotalsRowItemStyled visibleRightBorder>
						<AlignRight>
							<TextV2
								data-testid={
									VALUATION_ELEMENT_ID.SOURCES_AND_USES_TABLE_TOTAL_VALUE_CONVERTED_AMOUNT
								}
								variant="sans/14/600"
							>
								{totalRow.values?.convertedAmount ?? ''}
							</TextV2>
						</AlignRight>
					</SourcesUsesTotalsRowItemStyled>
					<SourcesUsesTotalsRowItemStyled />
				</SourcesUsesTotalsRowStyled>
			)}
		</div>
	);
};
